import { useState, useRef, useEffect } from "react";
import { ArrowLeft, Camera, ChevronLeft, ChevronRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { InteractivePhotoPreview } from "@/components/uploader/InteractivePhotoPreview";
import { MetadataForm, PhotoMetadata } from "@/components/uploader/MetadataForm";
import { PhotoPrepareCompact } from "@/components/uploader/PhotoPrepareCompact";
import { PhotoStatus } from "@/components/uploader/PhotoPrepare";
import { Badge } from "@/components/ui/badge";
import { useToast } from "@/hooks/use-toast";
import { useFileManager } from "@/hooks/useFileManager";
import { useLocation } from "wouter";
import { useUser } from "@/contexts/UserContext";
import { api } from "@/config/api";
import { PhotoCacheService, PhotoAnalysisData } from "@/services/photoCacheService";
import { useEvent } from "@/events/hooks/useEvents";

interface PhotoProcessorProps {
  files: File[];
  onBack: () => void;
}

const PhotoProcessor = ({ files: propFiles, onBack }: PhotoProcessorProps) => {
  const { toast } = useToast();
  const { selectedFiles } = useFileManager();
  const [, setLocation] = useLocation();
  const { user, isLoading } = useUser();

  // Usar arquivos das props ou do contexto (compatibilidade)
  const files = (propFiles && propFiles.length > 0) ? propFiles : selectedFiles;

  // Estado para dados de análise do cache
  const [analysisData, setAnalysisData] = useState<PhotoAnalysisData[]>([]);

  // Redirecionar para login se não estiver autenticado
  useEffect(() => {
    if (!isLoading && !user) {
      setLocation('/login');
    }
  }, [user, isLoading, setLocation]);

  // Carregar dados do cache ao inicializar
  useEffect(() => {
    if (PhotoCacheService.hasCache()) {
      const data = PhotoCacheService.getAllAnalysisData();
      setAnalysisData(data);
      console.log('📦 Dados do cache carregados:', data.length, 'arquivos');
    } else {
      toast({
        title: "Cache não encontrado",
        description: "Nenhum cache encontrado. Retorne ao dashboard e inicie um novo processamento.",
        variant: "destructive",
      });
      setLocation('/uploader/dashboard');
    }
  }, []);

  // Estado para armazenar dados extraídos por OCR para cada arquivo
  const [extractedData, setExtractedData] = useState<Record<number, { bibNumber?: string; athleteId?: string }>>({});

  // Estado para gerenciar o status de processamento das fotos
  const [photoStatuses, setPhotoStatuses] = useState<PhotoStatus[]>([]);
  const [isUploading, setIsUploading] = useState(false);

  // Estado para gerenciar faces detectadas
  const [faceDetections, setFaceDetections] = useState<Record<number, any[]>>({});

  // Estado para gerenciar batch de upload
  const [batchId, setBatchId] = useState<string | null>(null);
  const [batchName, setBatchName] = useState<string>('');
  const [batchProgress, setBatchProgress] = useState({ processed: 0, failed: 0, total: 0 });

  // Obter eventId do cache e buscar informações do evento
  const cachedEventId = PhotoCacheService.getEventId();
  const { data: event, isLoading: eventLoading } = useEvent(cachedEventId || '');

  // Preencher automaticamente o batchName quando evento e usuário estiverem disponíveis
  useEffect(() => {
    if (event?.title && user?.name && !batchName) {
      const shortUserName = user.name.split(' ')[0]; // Pegar apenas o primeiro nome
      const autoBatchName = `${event.title} - ${shortUserName}`;
      setBatchName(autoBatchName);
      console.log('✅ Identificador preenchido automaticamente:', autoBatchName);
    }
  }, [event?.title, user?.name, batchName]);

  // Estado para controlar análise automática
  const [autoAnalysisInProgress, setAutoAnalysisInProgress] = useState<Record<number, boolean>>({});

  // Estado para metadados da foto atual (vem do localStorage)
  const [currentPhotoMetadata, setCurrentPhotoMetadata] = useState<PhotoMetadata | null>(null);

  const [currentFileIndex, setCurrentFileIndex] = useState(0);
  const currentFile = files[currentFileIndex];

  // Função para análise automática do PyImg usando cache
  const performAutoAnalysis = async (fileIndex: number) => {
    if (autoAnalysisInProgress[fileIndex]) {
      return; // Já está analisando
    }

    const file = files[fileIndex];
    if (!file) {
      console.error('❌ Arquivo não encontrado:', fileIndex);
      return;
    }

    // Verificar se já foi analisado no cache
    const cacheData = PhotoCacheService.getAllAnalysisData();
    const analysis = cacheData.find(data => data.fileIndex === fileIndex);

    if (analysis && analysis.isAnalyzed) {
      console.log('✅ Arquivo já analisado:', file.name);
      loadPhotoMetadataFromCache(fileIndex);
      return;
    }

    setAutoAnalysisInProgress(prev => ({ ...prev, [fileIndex]: true }));

    try {


      // Usar rota unificada com provider NodeJS nativo
      const { getApiBaseUrl } = await import('@/config/production');

      const formData = new FormData();
      formData.append('image', file);

      const response = await fetch(`${getApiBaseUrl()}/api/pyimg/analyze?provider=nodejs`, {
        method: 'POST',
        body: formData
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const result = await response.json();

      if (!result.success || !result.data?.technical) {
        throw new Error(result.error || 'Resposta inválida da análise');
      }

      // Converter para formato compatível com o cache
      const analysisResult = {
        technical: result.data.technical,
        warnings: result.data.warnings || []
      };

      console.log('🔍 Dados da análise NodeJS recebidos:', {
        camera: analysisResult.technical.camera,
        camera_make: analysisResult.technical.camera_make,
        author: analysisResult.technical.author,
        capture_datetime: analysisResult.technical.capture_datetime,
        width: analysisResult.technical.width,
        height: analysisResult.technical.height,
        file_size_bytes: analysisResult.technical.file_size_bytes,
        provider: 'NodeJS Native',
        warnings: analysisResult.warnings
      });

      // Atualizar dados no cache
      PhotoCacheService.updateAnalysisData(fileIndex, analysisResult);

      // Converter resultado para PhotoMetadata
      const photoMetadata: PhotoMetadata = {
        originalFilename: file.name,
        cameraModel: analysisResult.technical.camera || analysisResult.technical.camera_make || '',
        resolution: `${analysisResult.technical.width}x${analysisResult.technical.height}`,
        qualityScore: 0,
        bibNumbers: [], // Será preenchido do cache se existir
        athleteIds: [], // Removido - não será usado
        tags: [], // Removido - não será usado
        eventIds: [], // Removido - não será usado
        photographerId: user?.id || '',
        technical: analysisResult.technical
      };

      // Atualizar dados no cache (preservando dados manuais)
      PhotoCacheService.updateAnalysisData(fileIndex, analysisResult);

      // Carregar dados atualizados do cache para incluir dados manuais
      const updatedAnalysis = PhotoCacheService.getAnalysisDataByIndex(fileIndex);
      if (updatedAnalysis) {
        photoMetadata.bibNumbers = updatedAnalysis.bibNumbers || [];
      }

      // Atualizar estado local
      setCurrentPhotoMetadata(photoMetadata);

      // Atualizar status da foto para ready automaticamente
      setPhotoStatuses(prev => prev.map((photo, idx) =>
        idx === fileIndex ? {
          ...photo,
          status: 'ready' as const,
          metadata: {
            cameraModel: photoMetadata.cameraModel,
            resolution: photoMetadata.resolution,
            qualityScore: photoMetadata.qualityScore,
            bibNumbers: photoMetadata.bibNumbers,
            athleteIds: photoMetadata.athleteIds,
            tags: photoMetadata.tags,
            eventIds: photoMetadata.eventIds,
            photographerId: photoMetadata.photographerId,
            technical: photoMetadata.technical
          }
        } : photo
      ));



    } catch (error) {
      console.error('❌ Erro na análise automática:', error);
      const errorMessage = error instanceof Error ? error.message : String(error);
      const errorStack = error instanceof Error ? error.stack : undefined;
      console.error('📊 Detalhes técnicos:', {
        fileName: file.name,
        fileSize: file.size,
        fileType: file.type,
        error: errorMessage,
        stack: errorStack
      });
      // Toast removido - erro de análise não precisa notificar o usuário
    } finally {
      setAutoAnalysisInProgress(prev => ({ ...prev, [fileIndex]: false }));
    }
  };

  // Função para carregar metadados do cache
  const loadPhotoMetadataFromCache = (fileIndex: number) => {
    const cacheData = PhotoCacheService.getAllAnalysisData();
    const analysis = cacheData.find(data => data.fileIndex === fileIndex);

    if (analysis && analysis.isAnalyzed) {
      const photoMetadata: PhotoMetadata = {
        originalFilename: analysis.fileName,
        cameraModel: analysis.cameraModel || '',
        resolution: analysis.resolution || '',
        qualityScore: 0,
        bibNumbers: analysis.bibNumbers || [],
        athleteIds: [], // Removido - não será usado
        tags: [], // Removido - não será usado
        eventIds: [], // Removido - não será usado
        photographerId: user?.id || '', // Removido do cache, usando do contexto
        technical: {
          camera: analysis.cameraModel,
          camera_make: analysis.cameraModel,
          author: analysis.author,
          capture_datetime: analysis.captureData,
          width: analysis.width,
          height: analysis.height,
          file_size_bytes: analysis.fileSize
        }
      };

      setCurrentPhotoMetadata(photoMetadata);
      console.log('📂 Metadados carregados do cache:', analysis.fileName);

      // Atualizar status da foto para ready automaticamente
      setPhotoStatuses(prev => prev.map((photo, idx) =>
        idx === fileIndex ? {
          ...photo,
          status: 'ready' as const,
          metadata: {
            cameraModel: photoMetadata.cameraModel,
            resolution: photoMetadata.resolution,
            qualityScore: photoMetadata.qualityScore,
            bibNumbers: photoMetadata.bibNumbers,
            athleteIds: photoMetadata.athleteIds,
            tags: photoMetadata.tags,
            eventIds: photoMetadata.eventIds,
            photographerId: photoMetadata.photographerId,
            technical: photoMetadata.technical
          }
        } : photo
      ));
    }
  };

  // Efeito para análise automática ao trocar de foto
  useEffect(() => {
    if (files.length > 0 && currentFileIndex < files.length) {
      const file = files[currentFileIndex];

      // Verificar se já tem análise no cache
      const cacheData = PhotoCacheService.getAllAnalysisData();
      const analysis = cacheData.find(data => data.fileIndex === currentFileIndex);

      if (analysis && analysis.isAnalyzed) {
        console.log('📂 Carregando metadados do cache para:', file.name);
        loadPhotoMetadataFromCache(currentFileIndex);
      } else if (!autoAnalysisInProgress[currentFileIndex]) {
        console.log('🔍 Executando análise PyImg para:', file.name);
        performAutoAnalysis(currentFileIndex);
      }
    }
  }, [currentFileIndex, files.length]);

  // Inicializar status das fotos
  useEffect(() => {
    if (files.length > 0) {
      // Resetar estados locais
      setCurrentPhotoMetadata(null);
      setAutoAnalysisInProgress({});

      const initialStatuses: PhotoStatus[] = files.map((file, index) => ({
        fileIndex: index,
        fileName: file.name,
        status: 'pending'
      }));
      setPhotoStatuses(initialStatuses);
    }
  }, [files]);

  const handleMetadataSave = (metadata: PhotoMetadata) => {


    // Salvar dados manuais no cache
    PhotoCacheService.updateManualData(currentFileIndex, {
      bibNumbers: metadata.bibNumbers || []
    });

    // Atualizar estado local
    setCurrentPhotoMetadata(metadata);

    // Atualizar status da foto para "ready" com TODOS os metadados
    setPhotoStatuses(prev => prev.map(photo =>
      photo.fileIndex === currentFileIndex
        ? {
          ...photo,
          status: 'ready' as const,
          metadata: {
            // Metadados básicos
            originalFilename: metadata.originalFilename,
            cameraModel: metadata.cameraModel,
            resolution: metadata.resolution,
            qualityScore: metadata.qualityScore,

            // Arrays de dados
            bibNumbers: metadata.bibNumbers,
            athleteIds: metadata.athleteIds,
            tags: metadata.tags,
            eventIds: metadata.eventIds,

            // IDs e dados técnicos
            photographerId: (user?.id || ''), // Sempre usar o ID do usuário logado
            technical: metadata.technical
          },
          processedAt: new Date()
        }
        : photo
    ));


  };

  const handleTextExtracted = (text: string, metadata?: { bibNumber?: string; athleteId?: string }) => {
    console.log("OCR result for", currentFile.name, ":", text);
    console.log("Extracted metadata:", metadata);

    // Armazena os dados extraídos para o arquivo atual
    if (metadata && (metadata.bibNumber || metadata.athleteId)) {
      setExtractedData(prev => ({
        ...prev,
        [currentFileIndex]: {
          ...prev[currentFileIndex],
          ...metadata
        }
      }));
    }

    toast({
      title: "Texto extraído",
      description: `OCR encontrou: "${text}"${metadata?.bibNumber ? ` | Bib: ${metadata.bibNumber}` : ''}${metadata?.athleteId ? ` | ID: ${metadata.athleteId}` : ''}`,
    });
  };

  // Gerenciar faces detectadas
  const handleFaceDetectionUpdate = (faces: any[]) => {
    setFaceDetections(prev => ({
      ...prev,
      [currentFileIndex]: faces
    }));
  };

  // Gerenciar marcação de bib number
  const handleBibNumberMarked = (bibNumber: string, area: { x: number; y: number; width: number; height: number }) => {
    console.log("Bib number marked:", bibNumber, "in area:", area);

    // Obter bibNumbers atuais do cache
    const currentAnalysis = PhotoCacheService.getAnalysisDataByIndex(currentFileIndex);
    const currentBibNumbers = currentAnalysis?.bibNumbers || [];

    // Adicionar novo bibNumber se não existir
    if (!currentBibNumbers.includes(bibNumber)) {
      const updatedBibNumbers = [...currentBibNumbers, bibNumber];

      // Atualizar no cache
      PhotoCacheService.updateManualData(currentFileIndex, {
        bibNumbers: updatedBibNumbers
      });

      // Atualizar estado local
      setCurrentPhotoMetadata(prev => prev ? {
        ...prev,
        bibNumbers: updatedBibNumbers
      } : null);

      // Atualizar status da foto
      setPhotoStatuses(prev => prev.map(photo =>
        photo.fileIndex === currentFileIndex
          ? {
            ...photo,
            metadata: {
              ...photo.metadata,
              bibNumbers: updatedBibNumbers
            }
          }
          : photo
      ));
    }

    toast({
      title: "Bib Number Adicionado",
      description: `Bib ${bibNumber} foi adicionado aos metadados da foto.`,
    });
  };

  // Gerenciar extração de texto da área selecionada
  const handleTextExtractedFromArea = (text: string, area: { x: number; y: number; width: number; height: number }) => {
    console.log("Text extracted from area:", text, "area:", area);
    // Aqui você pode adicionar lógica para processar o texto extraído da área
  };

  const goToPrevious = () => {
    setCurrentFileIndex(prev => Math.max(0, prev - 1));
  };

  const goToNext = () => {
    setCurrentFileIndex(prev => Math.min(files.length - 1, prev + 1));
  };

  const createBatch = async () => {
    if (!user?.id) {
      throw new Error('Usuário não autenticado');
    }

    const batchData = {
      photographer_id: user.id,
      batch_name: batchName.trim() || `Lote ${new Date().toLocaleString('pt-BR')}`,
      total_files: files.length,
      status: 'Processing'
    };

    const response = await api.post('/api/photo-batches', batchData);
    if (response.data.success) {
      return response.data.data.id;
    } else {
      throw new Error('Erro ao criar lote');
    }
  };

  const deleteBatch = async (batchIdToDelete: string) => {
    if (!batchIdToDelete) {
      console.log('⚠️ Nenhum batch para deletar');
      return;
    }

    try {
      const response = await api.delete(`/api/photo-batches/${batchIdToDelete}`);
      if (response.data.success) {
        return true;
      } else {
        console.error(`❌ Erro ao deletar batch: ${response.data.message}`);
        return false;
      }
    } catch (error) {
      console.error(`❌ Erro ao deletar batch ${batchIdToDelete}:`, error);
      return false;
    }
  };

  const updateBatchProgress = async (processedFiles: number, failedFiles: number, status: string) => {
    if (!batchId) return;

    try {
      await api.put(`/api/photo-batches/${batchId}/progress`, {
        processed_files: processedFiles,
        failed_files: failedFiles,
        status
      });
    } catch (error) {
      console.error('Erro ao atualizar progresso do lote:', error);
    }
  };

  const handleUpload = async () => {
    console.log("Iniciando upload das fotos processadas...");
    setIsUploading(true);

    try {
      const { uploadService } = await import('../services/uploadService');

      // Enviar TODAS as fotos, independente do status
      const allPhotos = photoStatuses;


      // Usar o usuário do contexto principal (já autenticado)
      if (!user) {
        toast({
          title: "Usuário não autenticado",
          description: "Você precisa estar logado para fazer upload de fotos.",
          variant: "destructive",
        });
        return;
      }

      // 1. INICIAR PROCESSAMENTO SEM CRIAR BATCH AINDA
      console.log('📦 Iniciando processamento de fotos...');

      let successCount = 0;
      let errorCount = 0;
      let duplicateCount = 0;
      let batchId: string | null = null;
      const uploadResults: Array<{
        fileName: string;
        success: boolean;
        isDuplicate?: boolean;
        error?: string;
        duplicateReason?: string;
      }> = [];

      // 2. GARANTIR ANÁLISE PyImg PARA CADA ARQUIVO
      console.log('🔍 Verificando análises PyImg para todos os arquivos...');
      for (let i = 0; i < allPhotos.length; i++) {
        const photoStatus = allPhotos[i];
        const file = files[photoStatus.fileIndex];

        // Verificar se já tem análise no cache
        const cacheData = PhotoCacheService.getAllAnalysisData();
        const analysis = cacheData.find(data => data.fileIndex === photoStatus.fileIndex);

        if (!analysis || !analysis.isAnalyzed) {
          console.log(`⚠️ Arquivo ${file.name} não tem análise PyImg. Executando agora...`);
          try {
            await performAutoAnalysis(photoStatus.fileIndex);
            // Aguardar um pouco para o estado ser atualizado
            await new Promise(resolve => setTimeout(resolve, 1000));
          } catch (error) {
            console.error(`❌ Erro ao analisar ${file.name}:`, error);
          }
        } else {
          console.log(`✅ Arquivo ${file.name} já tem análise PyImg`);
        }
      }

      // 3. CRIAR BATCH PRIMEIRO (se houver fotos para processar)
      if (allPhotos.length > 0) {
        console.log('📦 Criando batch antes do upload...');
        batchId = await createBatch();
        setBatchId(batchId);

        toast({
          title: "Lote criado",
          description: `Lote "${batchName.trim() || 'Sem nome'}" criado com sucesso.`,
        });

        console.log(`✅ Batch ${batchId} criado`);
      }

      // 4. PROCESSAR CADA FOTO COM BATCH ID
      for (const photoStatus of allPhotos) {
        try {
          const file = files[photoStatus.fileIndex];
          let metadata = photoStatus.metadata;

          // Priorizar dados do cache se disponível
          const cacheData = PhotoCacheService.getAllAnalysisData();
          const analysis = cacheData.find(data => data.fileIndex === photoStatus.fileIndex);

          if (analysis && analysis.isAnalyzed) {
            metadata = {
              cameraModel: analysis.cameraModel || '',
              resolution: analysis.resolution || '',
              qualityScore: 0,
              bibNumbers: analysis.bibNumbers || [],
              athleteIds: [],
              tags: [],
              eventIds: [],
              photographerId: user.id,
              technical: {
                camera: analysis.cameraModel,
                camera_make: analysis.cameraModel,
                author: analysis.author,
                capture_datetime: analysis.captureData,
                width: analysis.width,
                height: analysis.height,
                file_size_bytes: analysis.fileSize
              }
            };
          }

          // Se ainda não há metadados, criar metadados básicos
          if (!metadata) {
            // Tentar obter bib numbers do cache mesmo para metadados básicos
            const cacheData = PhotoCacheService.getAllAnalysisData();
            const analysis = cacheData.find(data => data.fileIndex === photoStatus.fileIndex);

            metadata = {
              cameraModel: '',
              resolution: 'Unknown',
              qualityScore: 0,
              bibNumbers: analysis?.bibNumbers || [],
              athleteIds: [],
              tags: [],
              eventIds: [],
              photographerId: user?.id || '',
              technical: {}
            };
          }

          // Obter eventId do cache se disponível
          const cachedEventId = PhotoCacheService.getEventId();

          // Preparar metadados para o upload (COM BATCH ID E EVENT ID)
          // IMPORTANTE: Sempre usar o ID do fotógrafo logado, não o criador do evento
          const uploadMetadata = {
            eventId: cachedEventId || metadata?.eventIds?.[0] || undefined,
            photographerId: user.id, // Sempre usar o ID do usuário logado
            batchId: batchId || undefined, // ← INCLUIR BATCH ID
            bibNumbers: metadata?.bibNumbers || [],
            athleteIds: metadata?.athleteIds || [],
            tags: metadata?.tags || [],
            cameraModel: metadata?.cameraModel || '',
            resolution: metadata?.resolution || '',
            technical: metadata?.technical || {},
            author: metadata?.technical?.author || '',
            captureData: metadata?.technical?.capture_datetime || '',
            originalFilename: file.name
          };

          // Upload da foto usando o backend principal
          console.log(`📤 Fazendo upload da foto: ${file.name} com batchId: ${batchId}`);
          const uploadResult = await uploadService.uploadPhotoWithMetadata(file, uploadMetadata);

          if (uploadResult.success) {
            successCount++;
            uploadResults.push({
              fileName: file.name,
              success: true
            });

            // Atualizar status da foto
            setPhotoStatuses(prev => prev.map(photo =>
              photo.fileIndex === photoStatus.fileIndex
                ? { ...photo, status: 'uploaded' as const, uploadedAt: new Date() }
                : photo
            ));


          } else if (uploadResult.isDuplicate) {
            duplicateCount++;
            uploadResults.push({
              fileName: file.name,
              success: false,
              isDuplicate: true,
              duplicateReason: uploadResult.duplicateReason || 'Arquivo duplicado'
            });

            // Atualizar status da foto para duplicate
            setPhotoStatuses(prev => prev.map(photo =>
              photo.fileIndex === photoStatus.fileIndex
                ? { ...photo, status: 'duplicate' as const }
                : photo
            ));

            console.log(`🔄 Foto ${file.name} é duplicata: ${uploadResult.duplicateReason}`);
          } else {
            errorCount++;
            uploadResults.push({
              fileName: file.name,
              success: false,
              error: uploadResult.error || 'Erro desconhecido'
            });

            // Atualizar status da foto para error
            setPhotoStatuses(prev => prev.map(photo =>
              photo.fileIndex === photoStatus.fileIndex
                ? { ...photo, status: 'error' as const }
                : photo
            ));

            console.log(`❌ Erro no upload da foto ${file.name}: ${uploadResult.error}`);
          }

        } catch (error) {
          errorCount++;
          uploadResults.push({
            fileName: photoStatus.fileName,
            success: false,
            error: error instanceof Error ? error.message : 'Erro desconhecido'
          });

          // Atualizar status da foto para error
          setPhotoStatuses(prev => prev.map(photo =>
            photo.fileIndex === photoStatus.fileIndex
              ? { ...photo, status: 'error' as const }
              : photo
          ));

          console.error(`❌ Erro ao processar foto ${photoStatus.fileName}:`, error);
        }

        // Atualizar progresso
        setBatchProgress({ processed: successCount, failed: errorCount, total: files.length });
      }

      // 5. DECIDIR SE MANTÉM O BATCH BASEADO NOS RESULTADOS
      if (successCount === 0) {
        // Nenhuma foto foi enviada - remover batch se criado
        if (batchId) {
          console.log('❌ Nenhuma foto foi enviada - removendo batch criado');
          const deleted = await deleteBatch(batchId);
          if (deleted) {
            setBatchId(null);
            console.log('✅ Batch removido com sucesso');
          } else {
            console.log('⚠️ Falha ao remover batch, mas continuando...');
          }
        }
      }

      // 5. GERAR NOTIFICAÇÃO DETALHADA
      let notificationMessage = '';
      let notificationVariant: "default" | "destructive" = "default";

      if (successCount === 0) {
        if (duplicateCount === files.length) {
          notificationMessage = `🔄 Todas as ${files.length} foto(s) são duplicatas e foram ignoradas`;
          notificationVariant = "destructive";
        } else {
          notificationMessage = `❌ Nenhuma foto foi enviada - ${errorCount} erro(s) encontrado(s)`;
          notificationVariant = "destructive";
        }
      } else if (duplicateCount > 0) {
        notificationMessage = `✅ ${successCount} foto(s) enviada(s), ${duplicateCount} duplicata(s) ignorada(s)`;
      } else {
        notificationMessage = `✅ ${successCount} foto(s) enviada(s) com sucesso`;
      }

      toast({
        title: "Upload concluído",
        description: notificationMessage,
        variant: notificationVariant,
      });

      // Redirecionar para dashboard original com informações do batch
      console.log(`🎯 Redirecionando para dashboard com lote: ${batchId || 'Nenhum'}`);
      setLocation('/uploader/dashboard');

    } catch (error) {
      console.error('Erro geral no upload:', error);
      toast({
        title: "Erro no upload",
        description: "Não foi possível enviar as fotos. Tente novamente.",
        variant: "destructive",
      });
    } finally {
      setIsUploading(false);
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-muted-foreground">Carregando...</p>
        </div>
      </div>
    );
  }

  if (!user) {
    return null; // useEffect já vai redirecionar
  }

  if (!files || files.length === 0) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <p className="text-muted-foreground mb-4">Nenhuma foto selecionada para processamento.</p>
          <Button onClick={() => setLocation('/uploader/dashboard')}>
            Voltar ao Dashboard
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full bg-background">
      {/* Header */}
      <header className="border-b border-border bg-card/50 backdrop-blur-sm sticky top-12 z-40 shadow-sm">
        <div className="w-full px-4 sm:px-6 py-3 sm:py-4">
          {/* Mobile Layout */}
          <div className="flex flex-col space-y-3 md:hidden">
            {/* Top Row: Back button and file counter */}
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <Button variant="ghost" onClick={() => setLocation('/uploader/dashboard')} className="p-2 h-9 w-9">
                  <ArrowLeft className="h-4 w-4" />
                </Button>
                <div className="flex items-center space-x-2">
                  <div className="p-1.5 bg-gradient-to-br from-purple-500 to-pink-500 rounded-lg shadow-md">
                    <Camera className="h-4 w-4 text-white" />
                  </div>
                  <Badge variant="secondary" className="px-2 py-0.5 text-xs">
                    {currentFileIndex + 1}/{files.length}
                  </Badge>
                </div>
              </div>
              <PhotoPrepareCompact
                photoStatuses={photoStatuses}
                onUpload={handleUpload}
                isUploading={isUploading}
              />
            </div>
            
            {/* Navigation */}
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={goToPrevious}
                  disabled={currentFileIndex === 0}
                  className="h-8"
                >
                  <ChevronLeft className="h-4 w-4" />
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={goToNext}
                  disabled={currentFileIndex === files.length - 1}
                  className="h-8"
                >
                  <ChevronRight className="h-4 w-4" />
                </Button>
                <span className="text-xs text-muted-foreground truncate max-w-[120px]">
                  {currentFile.name}
                </span>
              </div>
            </div>

            {/* Batch Name Input */}
            <div className="w-full">
              <Input
                id="batchName"
                value={batchName}
                onChange={(e) => setBatchName(e.target.value)}
                placeholder="Identificador do processo"
                className="w-full h-8 text-sm"
              />
            </div>
          </div>

          {/* Desktop Layout */}
          <div className="hidden md:flex items-center justify-between gap-4">
            <div className="flex items-center space-x-4 flex-shrink-0">
              <Button variant="ghost" onClick={() => setLocation('/uploader/dashboard')} className="p-2">
                <ArrowLeft className="h-5 w-5" />
              </Button>
              <div className="flex items-center space-x-3">
                <div className="p-2 bg-gradient-to-br from-purple-500 to-pink-500 rounded-lg shadow-lg shadow-purple-500/50">
                  <Camera className="h-6 w-6 text-white" />
                </div>
                <div className="flex flex-col">
                  <Badge variant="secondary" className="px-3 py-1 mb-1 w-fit">
                    {currentFileIndex + 1} de {files.length}
                  </Badge>
                  <div className="text-sm text-muted-foreground">
                    Fotógrafo: <span className="font-medium text-foreground">{user?.name || user?.fullName || 'Usuário'}</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Identificador do Processo */}
            <div className="flex items-center space-x-3 flex-1 max-w-md">
              <Label htmlFor="batchName" className="text-sm font-medium text-muted-foreground whitespace-nowrap">
                Identificador:
              </Label>
              <Input
                id="batchName"
                value={batchName}
                onChange={(e) => setBatchName(e.target.value)}
                placeholder="Ex: Evento Maratona 2024"
                className="flex-1"
              />
            </div>

            {/* File Navigation and Status */}
            <div className="flex items-center space-x-4 flex-shrink-0">
              <div className="flex items-center space-x-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={goToPrevious}
                  disabled={currentFileIndex === 0}
                >
                  <ChevronLeft className="h-4 w-4" />
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={goToNext}
                  disabled={currentFileIndex === files.length - 1}
                >
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>

              <div className="hidden lg:flex items-center space-x-2 text-sm text-muted-foreground">
                <span>Processing:</span>
                <span className="font-medium text-foreground max-w-48 truncate">{currentFile.name}</span>
              </div>

              {/* PhotoPrepareCompact */}
              <PhotoPrepareCompact
                photoStatuses={photoStatuses}
                onUpload={handleUpload}
                isUploading={isUploading}
              />
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="w-full px-4 sm:px-6 py-4 sm:py-6 lg:py-8 pb-8">
        <div className="grid grid-cols-1 lg:grid-cols-[1fr_0.43fr] gap-4 sm:gap-6 lg:gap-8 min-h-[calc(100vh-16rem)] lg:min-h-[calc(100vh-14rem)]">
          {/* Photo Preview with OCR */}
          <div className="space-y-4 min-h-[400px] lg:min-h-0">
            <InteractivePhotoPreview
              key={currentFileIndex}
              photo={currentFile}
              onTextExtracted={handleTextExtractedFromArea}
              onBibNumberMarked={handleBibNumberMarked}
              faceDetections={faceDetections[currentFileIndex] || []}
              onFaceDetectionUpdate={handleFaceDetectionUpdate}
            />
          </div>

          {/* Metadata Form */}
          <div className="space-y-4">
            <MetadataForm
              key={currentFileIndex}
              photo={currentFile}
              onSave={handleMetadataSave}
              onTextExtracted={handleTextExtracted}
              initialData={{
                ...extractedData[currentFileIndex],
                bibNumbers: photoStatuses[currentFileIndex]?.metadata?.bibNumbers || []
              }}
              cachedMetadata={currentPhotoMetadata ?? undefined}
              isAutoAnalyzing={autoAnalysisInProgress[currentFileIndex]}
              onRefreshAnalysis={() => performAutoAnalysis(currentFileIndex)}
            />
          </div>
        </div>
      </main>
    </div>
  );
};

export default PhotoProcessor;
